import { useState, useEffect } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import { Box, Typography } from "@mui/material";

import { ActionButton } from "components/ActionButton";
import { FrmSelectScalarWithObjectOptions } from "components/formFields/FrmSelectScalarWithObjectOptions";
import { FrmTextField } from "components/formFields/FrmTextField";
import { ButtonProgress } from "components/utility/ButtonProgress";

import { i18n } from "services/i18nService";
import { getSystemUserCompanyData } from "services/sosInventoryService/sosApi";
import { trialSignup } from "services/sosInventoryService/sosApi";
import {
  FOUND_US_VIA_OPTIONS,
  EMPTY_RECORD,
} from "services/sosInventoryService/trial/schema";
import { setBearerToken } from "services/utility/authentication";
import { getCompanyData } from "services/utility/newAuthentication";

import { openAlert } from "globalState/alertSlice";
import { setNeedsCompanyChooser } from "globalState/authenticationSlice";
import {
  setCompany,
  setCompanyList,
} from "globalState/userCompanySettingsSlice";

export function TrialSignup(props) {
  const { plan } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [allowSubmit, setAllowSubmit] = useState(false);
  const [captchaError, setCaptchaError] = useState(false);

  const user = useSelector((state) => state.userCompanySettings.user);
  const { isLoggedIn: isLoggedInString } = useSelector(
    (state) => state.authentication
  );
  const isLoggedIn = isLoggedInString === "yes";

  const emptyRecord = {
    ...EMPTY_RECORD,
    email: isLoggedIn ? user.email : null,
  };
  const [record, setRecord] = useState(emptyRecord);

  const dispatch = useDispatch();
  const { search, pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (pathname === "/planselection") {
      navigate(`/trialsignup${search}`, { replace: true });
    }
  }, [pathname, navigate, search]);

  async function handleSubmit(e) {
    e?.preventDefault();

    if (!allowSubmit) {
      setCaptchaError(true);
      return;
    }
    setCaptchaError(false);
    const params = new URLSearchParams(search);
    params.delete("plan");

    const queryParamsObj = Object.fromEntries(params.entries());

    // transform object into array of single key/pair objects
    const queryParameters = Object.entries(queryParamsObj).map(
      ([key, value]) => ({ [key]: value })
    );
    setIsLoading(true);
    const { success, data } = await trialSignup({
      ...record,
      plan,
      queryParameters,
    });
    if (success) {
      const message = isLoggedIn
        ? i18n("trial.LoggedInSignupSuccess")
        : i18n("trial.LoggedOutSignupSuccess");
      dispatch(openAlert({ type: "success", message }));
      setRecord(emptyRecord);

      if (isLoggedIn && data?.access_token) {
        await getNewCompanyData();
        setBearerToken(data.access_token);
        setIsLoading(false);
        navigate("/");
      } else {
        setIsLoading(false);
        navigate("/login");
      }
    }
  }

  async function getNewCompanyData() {
    const response = await getSystemUserCompanyData();
    const { company, companies, needsCompanyChooser } =
      getCompanyData(response);
    dispatch(setNeedsCompanyChooser(needsCompanyChooser));
    dispatch(setCompanyList(companies));
    dispatch(setCompany(company));
    return;
  }

  function handleInputFieldChange(fieldName, value) {
    setRecord((prevRecord) => ({ ...prevRecord, [fieldName]: value }));
  }

  return (
    <>
      <Box sx={{ m: "2rem 4rem" }}>
        <Typography sx={{ mb: 1 }} variant="h3">
          {i18n("trial.ContactInformation")}
        </Typography>
        <Typography
          sx={{ maxWidth: "66ch" }}
          component="div"
          variant="subheader"
        >
          {i18n("trial.Signup.Subtitle")}
        </Typography>
        <Typography sx={{ maxWidth: "66ch", mt: 0.5 }}>
          {i18n(
            isLoggedIn
              ? "trial.CreateCompanyLinkedToUserAccount"
              : "trial.AlreadyHaveAnExistingAccount"
          )}
        </Typography>
        <Box sx={{ maxWidth: "380px", mt: 3 }}>
          <form onSubmit={handleSubmit}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
              <FrmTextField
                required
                name="email"
                type="email"
                label={i18n("frmLabel.Email")}
                value={record.email}
                onValueBlur={handleInputFieldChange}
                disabled={isLoggedIn}
              />
              {!isLoggedIn && (
                <FrmTextField
                  required
                  type="password"
                  name="password"
                  label={i18n("frmLabel.Password")}
                  value={record.password}
                  onValueBlur={handleInputFieldChange}
                />
              )}
              <Box sx={{ mt: 2 }}>
                <FrmTextField
                  required
                  name="name"
                  label={i18n("frmLabel.Name")}
                  value={record.name}
                  onValueBlur={handleInputFieldChange}
                />
              </Box>
              <FrmTextField
                required
                name="companyName"
                label={i18n("frmLabel.Company")}
                value={record.companyName}
                onValueBlur={handleInputFieldChange}
              />
              <FrmTextField
                required
                name="phone"
                label={i18n("frmLabel.PhoneNumber")}
                value={record.phone}
                onValueBlur={handleInputFieldChange}
              />
              <Box sx={{ mt: 2 }}>
                <FrmTextField
                  name="promoCode"
                  label={i18n("frmLabel.PromotionCode")}
                  value={record.promoCode}
                  onValueBlur={handleInputFieldChange}
                />
                <FrmSelectScalarWithObjectOptions
                  name="foundUsVia"
                  options={FOUND_US_VIA_OPTIONS}
                  label={i18n("frmLabel.WhereDidYouHearAboutUs")}
                  onValueChange={handleInputFieldChange}
                  value={record.foundUsVia}
                />
              </Box>
            </Box>
            <Box>
              <ReCAPTCHA
                style={{ marginTop: "1rem" }}
                sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY}
                onChange={(value) => setAllowSubmit(Boolean(value))}
              />
              {captchaError && (
                <Typography variant="caption" color="error">
                  {i18n("error.CaptchaVerificationRequired")}
                </Typography>
              )}
            </Box>
            <Box sx={{ display: "inline-block", mt: 1 }}>
              <div style={{ position: "relative", alignItems: "center" }}>
                <ActionButton type="submit" size="large" disabled={isLoading}>
                  {i18n("trial.StartTrial")}
                </ActionButton>
                {isLoading && <ButtonProgress />}
              </div>
            </Box>
          </form>
        </Box>
      </Box>
    </>
  );
}
